import { useEffect, useState, type FormEvent } from 'react'

/**
 * Signing in: email and password, then a six-digit code from a phone app.
 * The first time (or after a reset) the second screen instead asks for a new
 * password and shows the QR code to scan.
 *
 * A remembered browser that has sat unused, or not passed a code today, gets
 * only the code screen (`reverifyEmail` set).
 *
 * Plain fetch rather than tRPC: these routes set cookies, and they are the only
 * ones that answer a caller with no session.
 */

type Step = 'password' | 'code' | 'setup' | 'reverify'

async function post(path: string, body: unknown): Promise<{ ok: boolean; data: Record<string, unknown> }> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
  return { ok: res.ok, data }
}

export function Login({ onSignedIn, reverifyEmail }: { onSignedIn: () => void; reverifyEmail?: string }) {
  const [step, setStep] = useState<Step>(reverifyEmail ? 'reverify' : 'password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [setup, setSetup] = useState<{ qr: string; secret: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const restart = (message: string) => {
    setStep('password')
    setPassword('')
    setCode('')
    setSetup(null)
    setError(message)
  }

  const useAnotherAccount = async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {})
    restart('')
  }

  useEffect(() => {
    if (step !== 'setup') return
    void fetch('/api/auth/setup')
      .then(async (res) => {
        const data = (await res.json()) as { qr?: string; secret?: string; error?: string }
        if (!res.ok || !data.qr || !data.secret) return restart(data.error ?? 'Sign-in expired. Start again.')
        setSetup({ qr: data.qr, secret: data.secret })
      })
      .catch(() => restart('Could not reach Joker. Try again.'))
  }, [step])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError(null)

    if (step === 'setup' && newPassword !== confirm) {
      setError('The two passwords do not match.')
      return
    }

    setBusy(true)
    try {
      const { ok, data } =
        step === 'password'
          ? await post('/api/auth/login', { email, password })
          : step === 'code'
            ? await post('/api/auth/verify', { code })
            : step === 'reverify'
              ? await post('/api/auth/reverify', { code })
              : await post('/api/auth/setup', { password: newPassword, code })

      if (!ok) {
        if (data.restart) return restart(String(data.error))
        setError(String(data.error ?? 'Something went wrong. Try again.'))
        setCode('')
        return
      }
      if (step === 'password') {
        setPassword('')
        setStep(data.next === 'setup' ? 'setup' : 'code')
        return
      }
      onSignedIn()
    } catch {
      setError('Could not reach Joker. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login">
      <form className="panel login-card" onSubmit={submit}>
        <div className="brand">
          <span className="brand-card" aria-hidden="true">
            J
          </span>
          <b>Joker</b>
          <span className="sub">Oryx ops console</span>
        </div>

        {step === 'password' && (
          <>
            <label className="pfield">
              <span>Email</span>
              <input type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="pfield">
              <span>Password</span>
              <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
          </>
        )}

        {step === 'reverify' && <p className="login-note">Still you? Signed in as {reverifyEmail}.</p>}

        {(step === 'code' || step === 'reverify') && (
          <label className="pfield">
            <span>6-digit code from your phone app</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]{6,7}"
              maxLength={7}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
        )}

        {step === 'setup' && (
          <>
            <p className="login-note">First sign-in. Choose your own password, then link a phone app.</p>
            <label className="pfield">
              <span>New password (at least 12 characters)</span>
              <input type="password" autoComplete="new-password" required minLength={12} autoFocus value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </label>
            <label className="pfield">
              <span>New password again</span>
              <input type="password" autoComplete="new-password" required minLength={12} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </label>
            <p className="login-note">
              Scan this with Google Authenticator, 1Password or a similar app, then type the code it shows.
            </p>
            {setup ? (
              <>
                <img className="login-qr" src={setup.qr} alt="QR code for your phone app" width={220} height={220} />
                <p className="login-secret">
                  Can't scan? Enter this key: <code>{setup.secret}</code>
                </p>
              </>
            ) : (
              <p className="login-note">Loading…</p>
            )}
            <label className="pfield">
              <span>6-digit code</span>
              <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
          </>
        )}

        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}

        <button className="btn primary" type="submit" aria-disabled={busy}>
          {step === 'password' ? 'Continue' : 'Sign in'}
        </button>
        {step === 'reverify' ? (
          <button type="button" className="btn ghost" onClick={() => void useAnotherAccount()}>
            Use a different account
          </button>
        ) : (
          step !== 'password' && (
            <button type="button" className="btn ghost" onClick={() => restart('')}>
              Start again
            </button>
          )
        )}
      </form>
    </div>
  )
}
